import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import SMSRecruiterCard, { SMS_COPY } from "../../components/SMSRecruiterCard";
import PageMeta from "../../components/seo/PageMeta";
import {
  Lock, ShieldCheck, FileCheck, KeyRound, CheckCircle2, ArrowRight, ArrowUpRight,
  ClipboardCheck, Search, Stamp, Fingerprint, Archive, Eye, Activity,
  ShieldAlert, Scale, Network, Stethoscope, HeartHandshake, Microscope,
  Sparkles, BadgeCheck,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Section data — kept light to honour the "calm, not dense" tone     */
/* ------------------------------------------------------------------ */

const STANDARDS = [
  { k: "HIPAA",            d: "ePHI handled across storage, transit, and access" },
  { k: "SOC 2",            d: "Availability, confidentiality, processing integrity" },
  { k: "Joint Commission", d: "Credentialing and competency mapped to HR standards" },
  { k: "NCQA",             d: "Friendly to NCQA-aligned health-plan requirements" },
  { k: "OIG / SAM",        d: "Monthly federal exclusion-list screening" },
  { k: "State Boards",     d: "Primary-source license verification" },
];

const PHILOSOPHY = [
  { Icon: ShieldCheck, t: "Verify at the source",         d: "Licensure and national certifications are validated against issuing boards — never re-typed from a résumé." },
  { Icon: Scale,       t: "Enforce before submission",    d: "Facility-specific rule sets fire pre-submission. A non-compliant candidate never reaches a hiring manager's queue." },
  { Icon: Archive,     t: "Log everything, edit nothing", d: "Every credential action is written to an immutable ledger — ready for Joint Commission survey on demand." },
  { Icon: Eye,         t: "Least-privilege by default",   d: "Role-scoped permissions for clinicians, facilities, recruiters, and admins. Access is earned, not inherited." },
];

const ONBOARDING_STEPS = [
  { s: "01", Icon: Fingerprint,    t: "Identity & license",       d: "Government ID verified, RN/APP license primary-sourced against the issuing state board." },
  { s: "02", Icon: BadgeCheck,     t: "Certifications",            d: "BLS, ACLS, PALS, NIHSS, specialty certs validated against AHA / ARC / ANCC databases." },
  { s: "03", Icon: Search,         t: "Background & screenings",   d: "7-year background check, OIG / SAM / GSA exclusion screens, 10-panel drug, TB and vaccinations." },
  { s: "04", Icon: ClipboardCheck, t: "Facility onboarding",       d: "Unit-specific orientation, competency checklists, N95 fit-test, and facility-mandated modules." },
  { s: "05", Icon: Stamp,          t: "Compliance officer sign-off", d: "A candidate becomes submittable only after a named compliance officer clears the file. No overrides." },
];

const WORKFLOW_STAGES = [
  { t: "Intake",                      d: "Guided, device-agnostic document upload with field-level validation." },
  { t: "Primary-source verification", d: "Automated PSV against issuing boards; manual escalation for edge cases handled by credentialed specialists." },
  { t: "Facility rule match",         d: "Documents matched against each facility's unit-level requirements (TB cadence, competencies, orientation modules)." },
  { t: "Expiry monitoring",           d: "Alerts at 90 / 60 / 30-day thresholds with clinician-visible countdown and recruiter escalation." },
  { t: "Renewal & re-verification",   d: "Guided renewal with re-PSV — clinicians stay compliant without interrupting active assignments." },
];

const DOC_STANDARDS = [
  { Icon: Lock,        t: "AES-256 at rest",         d: "All documents encrypted in storage, with keys rotated on a defined cadence." },
  { Icon: Network,     t: "TLS 1.3 in transit",      d: "Legacy protocols disabled at the load balancer. No exceptions." },
  { Icon: FileCheck,   t: "Write-once audit ledger", d: "Append-only credential events. Historical states reconstructable for any survey date." },
  { Icon: Eye,         t: "Role-scoped visibility",  d: "Clinicians see their own; facilities see only their requisition candidates; admins see aggregates." },
  { Icon: Archive,     t: "Retention discipline",    d: "Policies aligned to state and federal mandates, with defensible deletion workflows." },
  { Icon: KeyRound,    t: "Signed actions",          d: "Approvals, rejections, and policy changes are cryptographically signed to an individual user." },
];

const AUDIT_PILLARS = [
  { t: "Competency assessment",         d: "Specialty checklists aligned with Joint Commission HR.01.05.03 — signed by recruiter and hiring facility." },
  { t: "Primary-source verification",   d: "HR.01.02.05 verification of licensure, education, training, and experience before first assignment." },
  { t: "Orientation documentation",     d: "Facility-specific orientation records stored and retrievable for the full retention window." },
  { t: "Health screening",              d: "Annual and pre-engagement screening (TB, vaccinations, N95 fit) tracked per IC.02.04.01." },
  { t: "Ongoing competency",            d: "CE and renewals tracked with lapse detection and automated escalation." },
  { t: "Sanction monitoring",           d: "Monthly OIG / SAM / GSA screens on active clinicians, with immediate suspension on flag." },
];

const RISK_CONTROLS = [
  "Every submission gated by a pre-cleared facility rule set — human error becomes a workflow problem, not a policy one",
  "Immediate suspension on exclusion-list flag, license lapse, or background-check status change",
  "Single executive sponsor per account with named escalation paths written into the SLA",
  "Monthly program reviews include a compliance exception log, reviewed jointly with facility leadership",
  "Incident response runbook with 24-hour notification on security or compliance events",
  "Quarterly third-party audits of credentialing operations and data-handling controls",
];

const CHECKLIST_CARDS = [
  {
    slug: "nurse",
    Icon: HeartHandshake,
    eyebrow: "Nursing",
    title: "Nurse Skills Checklist",
    description: "ICU, OR, L&D, ED, Med-Surg, Telemetry, PACU — 50+ specialty modules with years-of-experience scoring.",
    specs: "50+ specialties · RN / LPN",
  },
  {
    slug: "therapist",
    Icon: Stethoscope,
    eyebrow: "Therapy",
    title: "Therapist Skills Checklist",
    description: "PT, OT, SLP across acute, inpatient rehab, SNF, home health, pediatric, and school-based settings.",
    specs: "PT · OT · SLP · COTA · PTA",
  },
  {
    slug: "other",
    Icon: Microscope,
    eyebrow: "Allied Health",
    title: "Allied & Other Checklists",
    description: "Imaging, laboratory, respiratory, surgical tech, and cardiovascular technologists with modality-specific scoring.",
    specs: "Imaging · Lab · Respiratory · Surg Tech",
  },
];

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function Compliance() {
  return (
    <div className="min-h-screen bg-white" data-testid="compliance-page">
      <PageMeta
        title="Compliance & Quality | TRITAL Care®"
        description="Credentialing discipline, documentation controls, and audit posture built for healthcare leadership. Joint Commission-aligned, HIPAA-aware, SOC 2 aligned."
        canonical="https://www.tritalcare.com/compliance"
      />
      <PublicNav />

      {/* 1 · HERO --------------------------------------------------- */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="compliance-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <ShieldCheck className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
              <span className="overline text-trital-navy">Compliance & Quality</span>
            </div>
            <h1 className="mt-6 font-display font-black text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-slate-900 text-balance">
              Compliance is the <span className="text-trital-navy">prerequisite.</span>
              <span className="block text-trital-red">Not the feature.</span>
            </h1>
            <p className="mt-6 text-slate-600 max-w-xl leading-relaxed">
              TRITAL Care® operates with the credentialing discipline, documentation controls, and audit posture healthcare leadership is required to defend — not the afterthought most staffing agencies bolt on.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="compliance-hero-contact"
              >
                Talk to our compliance team <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <a
                href="#audit-readiness"
                className="inline-flex items-center px-6 py-3.5 rounded-full border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50"
                data-testid="compliance-hero-standards"
              >
                Review our standards
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <div className="rounded-3xl border border-slate-200 bg-white shadow-navy p-7 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-trital-medical-blue-soft blur-3xl" />
              <div className="relative">
                <div className="overline text-trital-red">Standards alignment</div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {STANDARDS.map(({ k, d }) => (
                    <div key={k} className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="font-display font-black text-[15px] text-trital-navy">{k}</div>
                      <div className="mt-1 text-[11.5px] text-slate-600 leading-snug">{d}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2 · QUALITY PHILOSOPHY ------------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="compliance-philosophy">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">Our Quality Philosophy</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
            Four principles that govern how we operate.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
            Not a marketing framework — the actual decision rules our credentialing, clinical, and operations teams work by.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PHILOSOPHY.map(({ Icon, t, d }, i) => (
            <motion.div
              key={t}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
                <Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="mt-5 font-display font-bold text-[16px] leading-snug text-slate-900">{t}</div>
              <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3 · COMPLIANCE-LED ONBOARDING ------------------------------ */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="compliance-onboarding">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="max-w-3xl">
            <div className="overline text-trital-red">Compliance-Led Onboarding</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              Five gates. One sign-off.
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
              A candidate is not submittable until every stage clears. Compliance officers own the final release — not recruiters, not account managers.
            </p>
          </div>
          <div className="mt-12 relative">
            <div className="hidden lg:block absolute left-0 right-0 top-[30px] h-px bg-slate-200" />
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
              {ONBOARDING_STEPS.map(({ s, t, Icon, d }, i) => (
                <motion.div
                  key={s}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="relative"
                >
                  <div className="relative z-10 flex items-center gap-3 mb-4">
                    <div className="w-[60px] h-[60px] rounded-2xl bg-white border border-slate-200 shadow-card flex items-center justify-center">
                      <div className="font-display font-black text-xl text-trital-navy">{s}</div>
                    </div>
                    <Icon className="w-5 h-5 text-trital-red" strokeWidth={1.8} />
                  </div>
                  <div className="font-display font-bold text-[15px] text-slate-900">{t}</div>
                  <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4 · CREDENTIALING PROCESS --------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-10" data-testid="compliance-credentialing">
        <div className="lg:col-span-5">
          <div className="overline text-trital-red">Credentialing Process</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
            A lifecycle, not a checkbox.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Credentialing doesn't end at onboarding. We run expiry monitoring, renewal flows, and re-verification for the entire tenure of every clinician on platform.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-[12px] text-slate-600">
            <Activity className="w-4 h-4 text-trital-navy" strokeWidth={1.8} />
            <span>Avg turnaround on a standard credentialing packet: <span className="font-semibold text-slate-900">3–5 business days</span>.</span>
          </div>
        </div>
        <ol className="lg:col-span-7 space-y-3 relative">
          <div className="absolute left-[22px] top-2 bottom-2 w-px bg-slate-200 hidden md:block" />
          {WORKFLOW_STAGES.map((w, i) => (
            <li key={w.t} className="relative rounded-2xl bg-white border border-slate-200 p-5 pl-16 md:pl-20 shadow-card">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 md:left-3 w-9 h-9 rounded-full bg-trital-navy text-white font-display font-black text-[13px] flex items-center justify-center ring-4 ring-white">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-display font-bold text-slate-900 text-[15px]">{w.t}</div>
              <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">{w.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* 5 · DOCUMENTATION STANDARDS -------------------------------- */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="compliance-doc-standards">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="overline text-trital-red">Documentation Standards</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
                Every action — attributable and defensible.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate-600 leading-relaxed">
                Systems of record designed for the kind of moment your accreditation depends on — not the kind of export a marketing deck requires.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {DOC_STANDARDS.map(({ Icon, t, d }, i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card"
              >
                <div className="w-11 h-11 rounded-xl bg-trital-navy text-white flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-5 font-display font-bold text-[16px] text-slate-900">{t}</div>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 · AUDIT-READINESS --------------------------------------- */}
      <section id="audit-readiness" className="max-w-7xl mx-auto px-5 lg:px-8 py-24 scroll-mt-24" data-testid="compliance-audit">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">Audit-Readiness</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
            Mapped to the standards your surveyors reference.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            Our credentialing and quality workflows are built around Joint Commission HR and IC standards — not a looser agency interpretation.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {AUDIT_PILLARS.map((s) => (
            <div key={s.t} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
              <div className="flex items-center gap-2">
                <Stamp className="w-4 h-4 text-trital-navy" strokeWidth={1.8} />
                <div className="font-display font-bold text-[15px] text-slate-900">{s.t}</div>
              </div>
              <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 inline-flex items-center gap-2 text-[12.5px] text-slate-500 px-3 py-2 rounded-full bg-slate-50 border border-slate-200">
          <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
          TRITAL Care is independently reviewed against Joint Commission standards quarterly.
        </div>
      </section>

      {/* 7 · RISK REDUCTION & ACCOUNTABILITY ------------------------ */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden" data-testid="compliance-risk">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline text-white/60">Risk Reduction & Accountability</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-white text-balance">
              We build the audit trail before you need it.
            </h2>
            <p className="mt-4 text-white/75 leading-relaxed max-w-xl">
              Compliance is a daily habit, not a quarterly scramble. Here's what accountability looks like on our paper.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["24-hr incident notification", "Quarterly third-party audits", "Named exec sponsor"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-white/10 text-white font-bold uppercase tracking-wider">
                  <ShieldAlert className="w-3 h-3" strokeWidth={1.8} /> {t}
                </span>
              ))}
            </div>
          </div>
          <ul className="lg:col-span-7 grid gap-3">
            {RISK_CONTROLS.map((r) => (
              <li key={r} className="rounded-xl border border-white/10 bg-white/5 backdrop-blur p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-trital-red mt-0.5 shrink-0" strokeWidth={1.8} />
                <span className="text-[14px] text-white/85 leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 8 · CHECKLIST HUB PREVIEW ---------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="compliance-checklist-preview">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="overline text-trital-red">Clinical Skill Checklists</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              Evidence, not adjectives.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
              Specialty-specific skill checklists let clinicians self-rate, recruiters verify, and facility leaders review — on the same page.
              Every module carries years-of-experience scoring and facility-visible history.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link
              to="/compliance/checklists"
              className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-trital-navy hover:gap-2 transition-all"
              data-testid="compliance-checklist-hub-link"
            >
              Open the full checklist hub <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {CHECKLIST_CARDS.map(({ slug, Icon, eyebrow, title, description, specs }, i) => (
            <motion.div
              key={slug}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
            >
              <Link
                to={`/compliance/checklists/${slug}`}
                className="group relative block h-full rounded-3xl bg-white border border-slate-200 p-7 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all overflow-hidden"
                data-testid={`compliance-checklist-card-${slug}`}
              >
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-trital-medical-blue-soft blur-2xl opacity-60 group-hover:opacity-90 transition-opacity" />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center group-hover:bg-trital-navy group-hover:text-white transition-colors">
                      <Icon className="w-5 h-5" strokeWidth={1.8} />
                    </div>
                    <span className="overline text-trital-red">{eyebrow}</span>
                  </div>
                  <h3 className="mt-5 font-display font-bold text-[20px] leading-snug text-slate-900 group-hover:text-trital-navy flex items-center gap-1.5">
                    {title}
                    <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-trital-red" strokeWidth={1.8} />
                  </h3>
                  <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{description}</p>

                  <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-[11.5px] font-semibold text-slate-500 uppercase tracking-wider">{specs}</span>
                    <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-trital-navy">
                      View checklist
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 9 · CTA BAND ---------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="compliance-cta-band">
        <div className="rounded-3xl bg-white border border-slate-200 p-8 lg:p-12 relative overflow-hidden shadow-card">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-trital-medical-blue-soft blur-3xl" />
          <div className="absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-red-100 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="overline text-trital-red">Next step</div>
              <h3 className="mt-3 font-display font-black text-3xl lg:text-5xl tracking-tight text-slate-900 text-balance">
                Request our compliance packet.
              </h3>
              <p className="mt-4 text-slate-600 max-w-xl leading-relaxed">
                We'll share our credentialing SOPs, sample audit exports, and references — no NDA gymnastics, no sales pressure.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="compliance-cta-packet"
              >
                Request packet <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to="/employers#request"
                className="inline-flex items-center px-6 py-3.5 rounded-full border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50"
                data-testid="compliance-cta-engage"
              >
                Start an engagement
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="compliance-sms">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.compliance.headline} subtext={SMS_COPY.compliance.sub} />
      </section>

      <PublicFooter />
    </div>
  );
}
